import { m } from '@pricedrop/i18n';
import * as React from 'react';
import { LoginForm } from '@/components/auth/login-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { authClient } from '@/lib/auth-client';

type PendingAction = (() => void | Promise<void>) | null;

interface AuthModalState {
  title: string;
  description: string;
  redirectTo?: string;
}

interface RequireAuthOptions {
  title?: string;
  description?: string;
  redirectTo?: string;
}

interface AuthModalContextValue {
  openAuthModal: (options?: RequireAuthOptions) => void;
  closeAuthModal: () => void;
  requireAuth: (action: () => void | Promise<void>, options?: RequireAuthOptions) => Promise<void>;
}

const AuthModalContext = React.createContext<AuthModalContextValue | null>(null);

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [state, setState] = React.useState<AuthModalState>({
    title: m.auth_modal_title(),
    description: m.auth_modal_description(),
  });
  const pendingActionRef = React.useRef<PendingAction>(null);

  const openAuthModal = React.useCallback((options?: RequireAuthOptions) => {
    setState({
      title: options?.title || m.auth_modal_title(),
      description: options?.description || m.auth_modal_description(),
      redirectTo: options?.redirectTo,
    });
    setIsOpen(true);
  }, []);

  const closeAuthModal = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleAuthSuccess = React.useCallback(async () => {
    setIsOpen(false);

    const pendingAction = pendingActionRef.current;
    pendingActionRef.current = null;

    if (pendingAction) {
      await pendingAction();
    }
  }, []);

  const requireAuth = React.useCallback(
    async (action: () => void | Promise<void>, options?: RequireAuthOptions) => {
      const { data: session } = await authClient.getSession();

      if (session) {
        await action();
        return;
      }

      pendingActionRef.current = action;
      openAuthModal(options);
    },
    [openAuthModal],
  );

  React.useEffect(() => {
    function onAuthRequired() {
      openAuthModal();
    }

    window.addEventListener('auth:required', onAuthRequired);
    return () => {
      window.removeEventListener('auth:required', onAuthRequired);
    };
  }, [openAuthModal]);

  React.useEffect(() => {
    if (!isOpen) return;

    async function onFocus() {
      try {
        const { data: session } = await authClient.getSession();
        if (session) {
          await handleAuthSuccess();
        }
      } catch {
        // noop
      }
    }

    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [isOpen, handleAuthSuccess]);

  const value = React.useMemo(
    () => ({
      openAuthModal,
      closeAuthModal,
      requireAuth,
    }),
    [openAuthModal, closeAuthModal, requireAuth],
  );

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{state.title}</DialogTitle>
            <DialogDescription>{state.description}</DialogDescription>
          </DialogHeader>
          <LoginForm compact redirectTo={state.redirectTo} onSuccess={handleAuthSuccess} />
        </DialogContent>
      </Dialog>
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const context = React.useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }

  return context;
}
